import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { API_URLS } from '../../../config/api';
import { Mail, RefreshCcw, Search, Send, X } from 'lucide-react';

const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState('');
  const [compose, setCompose] = useState({
    subject: '',
    from_name: '',
    from_email: '',
    test_email: '',
    test_only: false,
    html: '<h2>Merhaba,</h2><p>Bu bir bilgilendirme e-postasıdır.</p>'
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URLS.NEWSLETTER_LIST);
      const data = await res.json();
      if (Array.isArray(data)) setSubscribers(data);
      else setSubscribers([]);
    } catch (e) {
      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = subscribers.filter(s => !query || (s.email || '').toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">E-posta Bülteni</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">Bültene kayıt olan e-posta adresleri</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Badge className="bg-blue-100 text-blue-700 text-xs sm:text-sm">{subscribers.length} kayıt</Badge>
          <Button variant="outline" onClick={load} size="sm" className="text-xs sm:text-sm">
            <RefreshCcw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"/>
            Yenile
          </Button>
          <Button onClick={()=>setIsSendOpen(true)} size="sm" className="text-xs sm:text-sm">
            <Send className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"/>
            E-posta Gönder
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <CardTitle className="text-base sm:text-lg lg:text-xl">Kayıtlar</CardTitle>
            <div className="relative w-full sm:w-auto sm:max-w-xs">
              <Input 
                value={query} 
                onChange={(e)=>setQuery(e.target.value)} 
                placeholder="E-posta ara" 
                className="pl-8 text-xs sm:text-sm"
              />
              <Search className="w-3 h-3 sm:w-4 sm:h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-gray-500 text-sm">Yükleniyor...</div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-500 text-sm">Kayıt bulunamadı</div>
          ) : (
            <>
              {/* Mobile View - Card Layout */}
              <div className="block sm:hidden">
                <div className="divide-y divide-gray-200">
                  {filtered.map((s) => (
                    <div key={s.id} className="p-3 space-y-2">
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-500 flex-shrink-0"/>
                        <span className="text-sm text-gray-900 truncate break-all">{s.email}</span>
                      </div>
                      <div className="text-xs text-gray-600 pl-6">
                        {s.created_at ? new Date(s.created_at).toLocaleString('tr-TR') : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop View - Table Layout */}
              <div className="hidden sm:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-posta</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kayıt Tarihi</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {filtered.map((s) => (
                        <tr key={s.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <Mail className="w-4 h-4 text-gray-500 flex-shrink-0"/>
                              <span className="text-sm text-gray-900 truncate break-all">{s.email}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{s.created_at ? new Date(s.created_at).toLocaleString('tr-TR') : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {isSendOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 px-3 sm:px-5 py-3 sm:py-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-base sm:text-lg font-semibold">E-posta Gönder</h3>
              <button onClick={()=>setIsSendOpen(false)} className="p-1 hover:bg-gray-100 rounded self-end sm:self-auto">
                <X className="w-4 h-4 sm:w-5 sm:h-5"/>
              </button>
            </div>
            <div className="p-3 sm:p-5 space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="text-xs sm:text-sm text-gray-600 block mb-1">Konu</label>
                  <Input 
                    value={compose.subject} 
                    onChange={e=>setCompose(v=>({...v, subject:e.target.value}))} 
                    placeholder="Bülten - Eylül 2025"
                    className="text-xs sm:text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm text-gray-600 block mb-1">Gönderen Adı</label>
                  <Input 
                    value={compose.from_name} 
                    onChange={e=>setCompose(v=>({...v, from_name:e.target.value}))} 
                    placeholder="Website Al"
                    className="text-xs sm:text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm text-gray-600 block mb-1">Gönderen E-posta</label>
                  <Input 
                    value={compose.from_email} 
                    onChange={e=>setCompose(v=>({...v, from_email:e.target.value}))} 
                    placeholder="noreply@website-al.com"
                    className="text-xs sm:text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm text-gray-600 block mb-1">Test E-posta</label>
                  <Input 
                    value={compose.test_email} 
                    onChange={e=>setCompose(v=>({...v, test_email:e.target.value}))} 
                    placeholder="test@ornek.com"
                    className="text-xs sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input id="test_only" type="checkbox" checked={compose.test_only} onChange={e=>setCompose(v=>({...v, test_only:e.target.checked}))} className="w-4 h-4"/>
                <label htmlFor="test_only" className="text-xs sm:text-sm text-gray-700">Sadece test e-postası gönder</label>
              </div>
              <div>
                <label className="text-xs sm:text-sm text-gray-600 block mb-1">E-posta İçeriği (HTML)</label>
                <textarea
                  className="w-full border rounded-md p-2 sm:p-3 min-h-[200px] sm:min-h-[280px] font-mono text-xs sm:text-sm"
                  value={compose.html}
                  onChange={e=>setCompose(v=>({...v, html:e.target.value}))}
                />
              </div>
              {sendStatus && (
                <div className={`text-xs sm:text-sm p-2 sm:p-3 rounded whitespace-pre-line ${
                  sendStatus.startsWith('Gönderildi:') && !sendStatus.includes('Hatalı: 0') 
                    ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' 
                    : sendStatus.startsWith('Hata:')
                    ? 'bg-red-50 text-red-800 border border-red-200'
                    : 'bg-green-50 text-green-800 border border-green-200'
                }`}>
                  {sendStatus}
                </div>
              )}
            </div>
            <div className="px-3 sm:px-5 py-3 sm:py-4 border-t flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 sm:gap-3 sticky bottom-0 bg-white">
              <Button variant="outline" onClick={()=>setIsSendOpen(false)} className="w-full sm:w-auto text-xs sm:text-sm">Vazgeç</Button>
              <Button disabled={sending} onClick={async ()=>{
                setSending(true); setSendStatus('');
                try {
                  const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=sendNewsletter`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(compose)
                  });
                  const json = await res.json();
                  if (json.status === 'success') {
                    let statusMsg = `Gönderildi: ${json.sent}, Hatalı: ${json.failed}`;
                    if (json.errors && json.errors.length > 0) {
                      statusMsg += '\n\nHata Detayları:\n';
                      json.errors.forEach((err, idx) => {
                        statusMsg += `${idx + 1}. ${err.email}: ${err.error}\n`;
                      });
                    }
                    setSendStatus(statusMsg);
                  } else {
                    setSendStatus('Hata: ' + (json.message || 'Gönderim başarısız'));
                  }
                } catch (e) {
                  setSendStatus('Hata: ' + e.message);
                } finally {
                  setSending(false);
                }
              }} className="w-full sm:w-auto text-xs sm:text-sm">
                {sending ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNewsletter;






